document.addEventListener('DOMContentLoaded', () => {
  const sections = Array.from(document.querySelectorAll('[data-vrs-return-form]'));
  if (!sections.length) return;

  let productCachePromise;
  const fileStateMap = new WeakMap();

  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const setSelectState = (select) => {
    if (!select) return;
    if (select.value && select.value !== '') {
      select.classList.add('is-selected');
    } else {
      select.classList.remove('is-selected');
    }
  };

  const applyFieldPresentation = (root = document) => {
    root.querySelectorAll('.vrs-return-request__field').forEach((field) => {
      const label = field.querySelector('.vrs-return-request__label');
      if (!label) return;

      const labelText = label.textContent.replace(/\s+/g, ' ').trim();
      const input = field.querySelector('.vrs-return-request__input');
      const textarea = field.querySelector('.vrs-return-request__textarea');
      const select = field.querySelector('.vrs-return-request__select');

      if (input) {
        if (input.dataset.vrsDateInit !== 'true' && input.type === 'date') {
          input.dataset.vrsDateInit = 'true';
          input.dataset.realPlaceholder = labelText;

          if (!input.value) {
            input.type = 'text';
            input.placeholder = labelText;
          }

          input.addEventListener('focus', () => {
            if (input.type === 'text') input.type = 'date';
          });

          input.addEventListener('blur', () => {
            if (!input.value) {
              input.type = 'text';
              input.placeholder = input.dataset.realPlaceholder || labelText;
            }
          });
        } else if (input.type !== 'date' && !input.getAttribute('placeholder')) {
          input.placeholder = labelText;
        }
      }

      if (textarea && !textarea.getAttribute('placeholder')) {
        textarea.placeholder = labelText;
      }

      if (select) {
        const firstOption = select.querySelector('option[value=""]');
        if (firstOption && (!firstOption.textContent || firstOption.textContent.trim() === '')) {
          firstOption.textContent = labelText;
        }
        setSelectState(select);
      }
    });
  };

  const fetchAllProducts = async () => {
    if (productCachePromise) return productCachePromise;

    productCachePromise = (async () => {
      const allProducts = [];
      let page = 1;
      const limit = 250;

      while (page <= 20) {
        const response = await fetch(`/products.json?limit=${limit}&page=${page}`, {
          credentials: 'same-origin'
        });

        if (!response.ok) break;

        const data = await response.json();
        const products = Array.isArray(data.products) ? data.products : [];

        if (!products.length) break;

        products.forEach((product) => {
          if (product && product.title) allProducts.push(product.title.trim());
        });

        if (products.length < limit) break;
        page += 1;
      }

      return [...new Set(allProducts)].sort((a, b) => a.localeCompare(b));
    })();

    return productCachePromise;
  };

  const fillProductDatalist = async (datalist) => {
    if (!datalist || datalist.dataset.loaded === 'true') return;

    try {
      const products = await fetchAllProducts();
      datalist.innerHTML = products.map((title) => `<option value="${escapeHtml(title)}"></option>`).join('');
      datalist.dataset.loaded = 'true';
    } catch (error) {
      datalist.dataset.loaded = 'true';
    }
  };

  const toggleVisibleGroup = (group, shouldShow) => {
    if (!group) return;

    group.hidden = !shouldShow;

    group.querySelectorAll('input, select, textarea').forEach((field) => {
      const mustRequire = field.dataset.requiredWhenVisible === 'true';
      field.disabled = !shouldShow;
      field.required = shouldShow && mustRequire;

      if (!shouldShow) {
        if (field.type !== 'checkbox' && field.type !== 'radio' && field.type !== 'file') {
          field.value = '';
        }

        if (field.tagName === 'SELECT') {
          field.selectedIndex = 0;
          setSelectState(field);
        }
      } else if (field.tagName === 'SELECT') {
        setSelectState(field);
      }
    });
  };

  const getValidJpgFiles = (input) =>
    Array.from(input.files || []).filter((file) => {
      const typeOk = /image\/jpeg/i.test(file.type);
      const nameOk = /\.jpe?g$/i.test(file.name || '');
      return typeOk || nameOk;
    });

  const setFilesOnInput = (input, files) => {
    if (typeof DataTransfer === 'undefined') {
      fileStateMap.set(input, files);
      return;
    }

    const dataTransfer = new DataTransfer();
    files.forEach((file) => dataTransfer.items.add(file));
    input.files = dataTransfer.files;
    fileStateMap.set(input, files);
  };

  sections.forEach((section) => {
    if (section.dataset.vrsReturnFormInit === 'true') return;
    section.dataset.vrsReturnFormInit = 'true';

    const firstName = section.querySelector('input[name="contact[First name]"]');
    const lastName = section.querySelector('input[name="contact[Last name]"]');
    const fullName = section.querySelector('[data-vrs-full-name]');
    const countrySelect = section.querySelector('[data-vrs-country-select]');
    const itemsContainer = section.querySelector('[data-vrs-items]');
    const template = section.querySelector('[data-vrs-item-template]');
    const addItemButton = section.querySelector('[data-vrs-add-item]');
    const datalist = section.querySelector('[data-vrs-product-datalist]');
    const itemCountField = section.querySelector('[data-vrs-item-count]');
    const itemSummaryField = section.querySelector('[data-vrs-item-summary]');
    const consentChecks = Array.from(section.querySelectorAll('[data-vrs-consent-checkbox]'));
    const submitButton = section.querySelector('[data-vrs-submit-button]');
    const itemToolbar = section.querySelector('.vrs-return-request__item-toolbar');
    const itemBlock = itemsContainer ? itemsContainer.closest('.vrs-return-request__block') : null;

    if (!itemsContainer || !template || !addItemButton || !itemSummaryField) return;

    applyFieldPresentation(section);
    fillProductDatalist(datalist);

    if (countrySelect) {
      const defaultCountry = (countrySelect.dataset.defaultCountry || '').trim();

      if (defaultCountry) {
        const match = Array.from(countrySelect.options).find(
          (option) => option.text.trim() === defaultCountry || option.value.trim() === defaultCountry
        );
        if (match) countrySelect.value = match.value;
      }

      setSelectState(countrySelect);
      countrySelect.addEventListener('change', () => setSelectState(countrySelect));
    }

    const syncFullName = () => {
      if (!fullName) return;

      const parts = [];
      if (firstName && firstName.value.trim()) parts.push(firstName.value.trim());
      if (lastName && lastName.value.trim()) parts.push(lastName.value.trim());
      fullName.value = parts.join(' ');
    };

    const getItems = () => Array.from(itemsContainer.querySelectorAll('[data-vrs-item]'));

    const syncAddItemPosition = () => {
      if (!itemToolbar || !itemBlock) return;

      if (getItems().length) {
        itemsContainer.insertAdjacentElement('afterend', itemToolbar);
        itemToolbar.classList.add('is-after-items');
      } else {
        itemBlock.insertBefore(itemToolbar, itemsContainer);
        itemToolbar.classList.remove('is-after-items');
      }
    };

    const updateSubmitState = () => {
      if (!submitButton) return;

      const hasItems = getItems().length > 0;
      const consentsOk = consentChecks.length > 0 && consentChecks.every((check) => check.checked);
      const enabled = hasItems && consentsOk;

      submitButton.disabled = !enabled;
      submitButton.classList.toggle('is-disabled', !enabled);
      submitButton.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    };

    const updateTitlesAndCount = () => {
      const items = getItems();

      items.forEach((item, index) => {
        const title = item.querySelector('[data-vrs-item-title]');
        if (title) title.textContent = `Item ${index + 1}`;
      });

      if (itemCountField) itemCountField.value = String(items.length);

      syncAddItemPosition();
      updateSubmitState();
    };

    const getItemConditionLines = (item) =>
      Array.from(item.querySelectorAll('[data-vrs-item-checkbox]:checked'))
        .map((checkbox) => checkbox.dataset.vrsItemCheckboxLabel || '')
        .filter(Boolean);

    const getInputFiles = (input) => fileStateMap.get(input) || Array.from(input.files || []);

    const buildSummary = () => {
      const lines = [];

      getItems().forEach((item, index) => {
        const product = item.querySelector('[data-vrs-item-product]')?.value.trim() || '';
        const quantity = item.querySelector('[data-vrs-item-quantity]')?.value || '';
        const size = item.querySelector('[data-vrs-item-size]')?.value || '';
        const variant = item.querySelector('[data-vrs-item-variant]')?.value.trim() || '';
        const checkedReason = item.querySelector('[data-vrs-item-request]:checked');
        const reason = checkedReason ? checkedReason.value : '';
        const returnDetail = item.querySelector('[data-vrs-item-return-detail]')?.value || '';
        const exchangeItem = item.querySelector('[data-vrs-item-exchange-item]')?.value.trim() || '';
        const exchangeSize = item.querySelector('[data-vrs-item-exchange-size]')?.value.trim() || '';
        const damageDescription = item.querySelector('[data-vrs-item-damage-description]')?.value.trim() || '';
        const wrongDescription = item.querySelector('[data-vrs-item-wrong-description]')?.value.trim() || '';
        const photoInput = item.querySelector('[data-vrs-item-photo]');
        const photoNames = photoInput ? getInputFiles(photoInput).map((file) => file.name).filter(Boolean) : [];
        const conditionLines = getItemConditionLines(item);

        lines.push(`Item ${index + 1}`);
        lines.push(`Product: ${product || '-'}`);
        lines.push(`Quantity: ${quantity || '-'}`);
        lines.push(`Size: ${size || '-'}`);
        lines.push(`Variant: ${variant || '-'}`);
        lines.push(`Reason: ${reason || '-'}`);

        if (reason === 'Return') {
          lines.push(`Return detail: ${returnDetail || '-'}`);
        }

        if (reason === 'Exchange') {
          lines.push(`Requested item change: ${exchangeItem || '-'}`);
          lines.push(`Requested size change: ${exchangeSize || '-'}`);
        }

        if (reason === 'Damaged / Defective item') {
          lines.push(`Damage description: ${damageDescription || '-'}`);
        }

        if (reason === 'Wrong / Missing item') {
          lines.push(`Wrong / missing item details: ${wrongDescription || '-'}`);
        }

        if (conditionLines.length) {
          lines.push(`Item condition: ${conditionLines.join('; ')}`);
        } else {
          lines.push('Item condition: -');
        }

        if (photoNames.length) {
          lines.push(`Selected JPG files: ${photoNames.join(', ')}`);
        }

        lines.push('');
      });

      itemSummaryField.value = lines.join('\n').trim();
    };

    const syncItemRequest = (item) => {
      const checked = item.querySelector('[data-vrs-item-request]:checked');
      const value = checked ? checked.value : '';

      const hiddenReason = item.querySelector('[data-vrs-item-request-hidden]');
      const returnGroup = item.querySelector('[data-vrs-item-group="return"]');
      const exchangeGroup = item.querySelector('[data-vrs-item-group="exchange"]');
      const damageGroup = item.querySelector('[data-vrs-item-group="damage"]');
      const wrongGroup = item.querySelector('[data-vrs-item-group="wrong"]');
      const returnDetailSelect = item.querySelector('[data-vrs-item-return-detail]');
      const returnDetailHidden = item.querySelector('[data-vrs-item-return-detail-hidden]');

      if (hiddenReason) hiddenReason.value = value;

      toggleVisibleGroup(returnGroup, value === 'Return');
      toggleVisibleGroup(exchangeGroup, value === 'Exchange');
      toggleVisibleGroup(damageGroup, value === 'Damaged / Defective item');
      toggleVisibleGroup(wrongGroup, value === 'Wrong / Missing item');

      if (returnDetailSelect) {
        setSelectState(returnDetailSelect);
      }

      if (returnDetailHidden) {
        returnDetailHidden.value = value === 'Return' && returnDetailSelect ? returnDetailSelect.value || '' : '';
      }
    };

    const renderPreviews = (input) => {
      const item = input.closest('[data-vrs-item]');
      if (!item) return;

      const previews = item.querySelector('[data-vrs-item-file-previews]');
      if (!previews) return;

      const currentFiles = getInputFiles(input);

      previews.innerHTML = '';

      const oldActions = item.querySelector('.vrs-return-request__file-actions');
      if (oldActions) oldActions.remove();

      if (!currentFiles.length) {
        previews.hidden = true;
        buildSummary();
        return;
      }

      previews.hidden = false;

      currentFiles.forEach((file, index) => {
        const url = URL.createObjectURL(file);

        const card = document.createElement('div');
        card.className = 'vrs-return-request__file-preview';

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'vrs-return-request__file-preview-remove';
        removeButton.setAttribute('aria-label', `Remove ${file.name}`);
        removeButton.textContent = '×';

        removeButton.addEventListener('click', () => {
          const nextFiles = getInputFiles(input).filter((_, fileIndex) => fileIndex !== index);
          setFilesOnInput(input, nextFiles);
          renderPreviews(input);
        });

        const image = document.createElement('img');
        image.className = 'vrs-return-request__file-preview-image';
        image.src = url;
        image.alt = file.name || 'Selected JPG preview';

        image.addEventListener('load', () => {
          URL.revokeObjectURL(url);
        });

        const name = document.createElement('div');
        name.className = 'vrs-return-request__file-preview-name';
        name.textContent = file.name || 'Selected JPG';

        card.appendChild(removeButton);
        card.appendChild(image);
        card.appendChild(name);
        previews.appendChild(card);
      });

      const actions = document.createElement('div');
      actions.className = 'vrs-return-request__file-actions';

      const clearButton = document.createElement('button');
      clearButton.type = 'button';
      clearButton.className = 'vrs-return-request__file-clear';
      clearButton.textContent = 'Remove all files';

      clearButton.addEventListener('click', () => {
        setFilesOnInput(input, []);
        renderPreviews(input);
      });

      actions.appendChild(clearButton);
      previews.insertAdjacentElement('afterend', actions);

      buildSummary();
    };

    const bindItem = (item) => {
      const removeButton = item.querySelector('[data-vrs-remove-item]');
      const watchFields = item.querySelectorAll('input, select, textarea');

      applyFieldPresentation(item);

      item.querySelectorAll('.vrs-return-request__select').forEach((select) => {
        setSelectState(select);

        select.addEventListener('change', () => {
          setSelectState(select);

          if (select.matches('[data-vrs-item-return-detail]')) {
            const hidden = item.querySelector('[data-vrs-item-return-detail-hidden]');
            if (hidden) hidden.value = select.value || '';
          }

          buildSummary();
        });
      });

      item.querySelectorAll('[data-vrs-item-request]').forEach((radio) => {
        radio.addEventListener('change', () => {
          syncItemRequest(item);
          buildSummary();
        });
      });

      if (removeButton) {
        removeButton.addEventListener('click', () => {
          item.remove();
          updateTitlesAndCount();
          buildSummary();
        });
      }

      watchFields.forEach((field) => {
        const eventName =
          field.type === 'file' || field.tagName === 'SELECT' || field.type === 'checkbox' || field.type === 'radio'
            ? 'change'
            : 'input';

        field.addEventListener(eventName, buildSummary);

        if (field.type !== 'checkbox' && field.type !== 'radio') {
          field.addEventListener('change', buildSummary);
        }

        if (field.matches('[data-vrs-item-photo]')) {
          field.addEventListener('change', () => {
            const files = getValidJpgFiles(field);
            setFilesOnInput(field, files);
            renderPreviews(field);
          });
        }
      });

      syncItemRequest(item);
      renderPreviews(item.querySelector('[data-vrs-item-photo]'));
      buildSummary();
    };

    let uid = getItems().length;

    const addItem = () => {
      uid += 1;

      const html = template.innerHTML.replace(/__INDEX__/g, String(uid));
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html.trim();

      const item = wrapper.firstElementChild;
      if (!item) return;

      itemsContainer.appendChild(item);
      bindItem(item);
      updateTitlesAndCount();
      buildSummary();
    };

    addItemButton.addEventListener('click', addItem);

    consentChecks.forEach((check) => {
      check.addEventListener('change', updateSubmitState);
    });

    if (firstName) firstName.addEventListener('input', syncFullName);
    if (lastName) lastName.addEventListener('input', syncFullName);

    getItems().forEach(bindItem);

    if (!getItems().length) {
      if (itemCountField) itemCountField.value = '0';
      if (itemSummaryField) itemSummaryField.value = '';
    }

    const itemObserver = new MutationObserver(() => {
      updateTitlesAndCount();
      buildSummary();
    });
    itemObserver.observe(itemsContainer, { childList: true });

    syncFullName();
    syncAddItemPosition();
    updateTitlesAndCount();
    buildSummary();
    updateSubmitState();
  });
});


/* ===== ORDER NUMBER HELP TOOLTIP ===== */

(function () {
  function initOrderNumberHelp(root) {
    const scope = root && root.querySelectorAll ? root : document;

    scope.querySelectorAll('[data-vrs-return-form] input[name="contact[Order number]"]').forEach((input) => {
      const field = input.closest('.vrs-return-request__field');
      if (!field || field.dataset.vrsOrderHelpInit === 'true') return;

      field.dataset.vrsOrderHelpInit = 'true';
      field.classList.add('vrs-return-request__field--order-help');

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'vrs-return-request__order-help-trigger';
      trigger.setAttribute('aria-label', 'Order number help');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.textContent = '?';

      const tooltip = document.createElement('div');
      tooltip.className = 'vrs-return-request__order-help-tooltip';
      tooltip.setAttribute('role', 'tooltip');
      tooltip.innerHTML = 'The order number usually looks something like <strong>#VRS1001</strong>. You can find it in your confirmation email.';

      field.appendChild(trigger);
      field.appendChild(tooltip);

      let pinned = false;

      function showTooltip() {
        tooltip.classList.add('is-visible');
        trigger.classList.add('is-active');
        trigger.setAttribute('aria-expanded', 'true');
      }

      function hideTooltip(force) {
        if (pinned && !force) return;
        tooltip.classList.remove('is-visible');
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
      }

trigger.addEventListener('mouseenter', function () {
  if (!pinned) showTooltip();
});

trigger.addEventListener('mouseleave', function () {
  if (!pinned) hideTooltip();
});

      trigger.addEventListener('focus', function () {
        showTooltip();
      });

      trigger.addEventListener('blur', function () {
        window.setTimeout(function () {
          if (!pinned && !field.contains(document.activeElement)) {
            hideTooltip();
          }
        }, 0);
      });

      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();

        pinned = !pinned;

        if (pinned) {
          showTooltip();
        } else {
          hideTooltip(true);
        }
      });

      tooltip.addEventListener('click', function (event) {
        event.stopPropagation();
      });

      document.addEventListener('click', function (event) {
        if (!field.contains(event.target)) {
          pinned = false;
          hideTooltip(true);
        }
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          pinned = false;
          hideTooltip(true);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initOrderNumberHelp(document);
    });
  } else {
    initOrderNumberHelp(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initOrderNumberHelp(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    initOrderNumberHelp(event.target);
  });
})();

/* ===== ORDER NUMBER HELP TOOLTIP CLICK FIX ===== */

document.addEventListener('click', function (event) {
  const trigger = event.target.closest('.vrs-return-request__order-help-trigger');
  if (!trigger) return;

  event.preventDefault();
  event.stopPropagation();

  const form = trigger.closest('form');
  if (form) {
    form.noValidate = true;
    setTimeout(function () {
      form.noValidate = false;
    }, 0);
  }
}, true);

